import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";

/**
 * Lets a builder step hook into the header's progress stepper.
 *
 * The stepper lives in the layout's header, but some steps need a say before
 * a forward step click goes through (Choose Photos starts line art, the cover
 * step starts the cover). A step registers an interceptor with
 * `useBuilderStepInterceptor`; `BuilderProgress` calls it with
 * `{ stepNumber, stepCode, route, navigate }` and the interceptor decides
 * whether to call `navigate()`.
 *
 * A step marks itself busy with `useBuilderBusy` while its photos are still
 * uploading or being prepared: the stepper and the header's Back then stay
 * put, since leaving the step would cancel the uploads.
 */
const BuilderNavContext = createContext(null);

export function BuilderNavProvider({ children }) {
  const interceptorRef = useRef(null);
  const backRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const value = useMemo(
    () => ({
      busy,
      setBusy,
      setInterceptor: (fn) => {
        interceptorRef.current = fn;
      },
      getInterceptor: () => interceptorRef.current,
      setBack: (fn) => {
        backRef.current = fn;
      },
      getBack: () => backRef.current,
    }),
    [busy]
  );
  return <BuilderNavContext.Provider value={value}>{children}</BuilderNavContext.Provider>;
}

/** Register (and on unmount clear) the step's interceptor for stepper clicks. */
export function useBuilderStepInterceptor(fn) {
  const ctx = useContext(BuilderNavContext);
  const latest = useRef(fn);
  latest.current = fn;
  const stable = useCallback((args) => latest.current?.(args), []);
  useEffect(() => {
    if (!ctx) return undefined;
    ctx.setInterceptor(stable);
    return () => ctx.setInterceptor(null);
  }, [ctx, stable]);
}

/**
 * Register the step's own Back handler for the header's Back button, so going
 * back from the header does the same bookkeeping as the bottom bar's Back.
 * The handler may return false to let the header fall back to its own route.
 */
export function useBuilderBack(fn) {
  const ctx = useContext(BuilderNavContext);
  const latest = useRef(fn);
  latest.current = fn;
  const stable = useCallback(() => latest.current?.(), []);
  useEffect(() => {
    if (!ctx) return undefined;
    ctx.setBack(stable);
    return () => ctx.setBack(null);
  }, [ctx, stable]);
}

/** Mark the step busy (photos uploading or being prepared) while `busy` is true. */
export function useBuilderBusy(busy) {
  const ctx = useContext(BuilderNavContext);
  const setBusy = ctx?.setBusy;
  useEffect(() => {
    if (!setBusy) return undefined;
    setBusy(!!busy);
    return () => setBusy(false);
  }, [setBusy, busy]);
}

export function useBuilderNav() {
  return useContext(BuilderNavContext);
}
